import { LucideProps } from "lucide-react";
import { BlockProps } from "../blocks/types";
import { Block, BlockType } from "../blocks";

/**
 * Form API provided to all block editors.
 * This allows blocks to manipulate form state without depending on react-hook-form.
 */
export type FormAPI = {
  blockPath: string;
  updateField: <V = unknown>(path: string, value: V) => void;
  getFieldValue: (path: string) => unknown;
  registerImage: (imageUploadKey: string, file: File | null) => void;
};

export enum BlockCategory {
  LAYOUT = "layout",
  ESSENTIALS = "essentials",
  TRACKING = "tracking",
  RETURN = "return",
}

export type Platform = "email" | "web";

export type BlockPanelProps<BP extends BlockProps> = BP & {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isWrapped?: boolean;
};

export type BlockRegisterInput<
  BP extends BlockProps,
  T extends BlockType = BlockType,
> = {
  type: T;
  name: string;
  description: string;
  Icon: React.FC<LucideProps>;
  category: BlockCategory;
  premium?: boolean;
  hasDynamicContent?: boolean;
  // If the block has a content, by default we will consider it is translated if content[locale] exists.
  // This method can be used to override this behavior
  isTranslated?: (locale: string, block: Block<T>) => boolean;
  components: Partial<
    Record<
      Platform,
      {
        component: React.FC<BP>;
        configPanel?: {
          component?: React.FC<BlockPanelProps<BP>>;
          features?: {
            delete?: boolean;
          };
        };
        editor: {
          component: React.FC<BP>;
        };
        compatibility?: {
          // Define if the block can be added inside a Row block
          rowBlock?:
            | {
                // Min size of the row block cell (1-12) for this block to be compatible
                minSize: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
              }
            | boolean;
        };
      }
    >
  >;
};

export function registerBlock<BP extends BlockProps>({
  components,
  category,
  premium,
  hasDynamicContent,
  ...config
}: BlockRegisterInput<BP>) {
  return {
    config: {
      ...config,
      category,
      premium,
      hasDynamicContent,
    },
    ...components,
    EmailComponent: components.email?.component,
    WebComponent: components.web?.component,
    EmailEditor: components.email?.editor.component,
    WebEditor: components.web?.editor.component,
    EmailConfigPanel: components.email?.configPanel?.component,
    WebConfigPanel: components.web?.configPanel?.component,
  };
}
